<template>
  <v-container fluid class="ebf-page pa-4 pa-md-6">
    <FundPageHeader
      title="Fund Settings"
      subtitle="Branch-wise eligibility, contribution and claim limits"
      icon="mdi-cog-outline"
    >
      <v-btn color="white" variant="tonal" prepend-icon="mdi-refresh" :loading="loading" @click="loadSettings">Reload</v-btn>
    </FundPageHeader>

    <v-alert
      v-if="loaded"
      :type="setupComplete ? 'success' : 'warning'"
      variant="tonal"
      class="mb-4"
      :icon="setupComplete ? 'mdi-check-circle-outline' : 'mdi-alert-outline'"
    >
      {{ setupComplete
        ? "At least one active fund bank/cash account is configured."
        : "Create an active fund bank/cash account before receiving or paying fund money." }}
    </v-alert>

    <v-card class="ebf-section-card" elevation="0" :loading="loading">
      <v-card-title>Policy configuration</v-card-title>
      <v-divider />
      <v-card-text class="pa-5">
        <v-form ref="formRef" v-model="valid" @submit.prevent="saveSettings">
          <v-row>
            <v-col cols="12" md="6">
              <v-text-field
                v-model.number="form.employee_pf_rate"
                type="number"
                min="0"
                max="100"
                step="0.01"
                label="Employee PF rate (%)"
                prepend-inner-icon="mdi-percent-outline"
                variant="outlined"
                density="compact"
                :rules="[rules.required, rules.percentage]"
              />
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field
                v-model.number="form.company_pf_rate"
                type="number"
                min="0"
                max="100"
                step="0.01"
                label="Company PF rate (%)"
                prepend-inner-icon="mdi-percent-outline"
                variant="outlined"
                density="compact"
                :rules="[rules.required, rules.percentage]"
              />
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field
                v-model.number="form.pf_vesting_years"
                type="number"
                min="0"
                label="PF company contribution vesting (years)"
                prepend-inner-icon="mdi-calendar-clock"
                variant="outlined"
                density="compact"
                :rules="[rules.required, rules.nonNegative]"
              />
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field
                v-model.number="form.gratuity_eligibility_years"
                type="number"
                min="0"
                label="Gratuity eligibility (years)"
                prepend-inner-icon="mdi-calendar-check-outline"
                variant="outlined"
                density="compact"
                :rules="[rules.required, rules.nonNegative]"
              />
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field
                v-model.number="form.medical_claims_per_year"
                type="number"
                min="1"
                label="Medical claims per year"
                prepend-inner-icon="mdi-medical-bag"
                variant="outlined"
                density="compact"
                :rules="[rules.positive]"
              />
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field
                v-model.number="form.medical_claim_limit"
                type="number"
                min="0.01"
                step="0.01"
                label="Maximum per medical claim"
                prepend-inner-icon="mdi-cash-multiple"
                variant="outlined"
                density="compact"
                :rules="[rules.positive]"
              />
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field
                v-model.number="form.loan_basic_salary_multiplier"
                type="number"
                min="0.01"
                step="0.01"
                label="Loan limit (basic salary multiplier)"
                prepend-inner-icon="mdi-calculator-variant-outline"
                variant="outlined"
                density="compact"
                :rules="[rules.positive]"
              />
            </v-col>
            <v-col cols="12">
              <v-switch v-model="form.status" color="success" label="Enable Employee Benefit Funds for this branch" hide-details />
            </v-col>
          </v-row>

          <div class="d-flex justify-end mt-4" v-if="$updateAccess($route.fullPath)">
            <v-btn type="submit" color="primary" prepend-icon="mdi-content-save-outline" :loading="saving">Save settings</v-btn>
          </div>
        </v-form>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script setup>
import { onMounted, reactive, ref } from "vue";
import { toast } from "vue3-toastify";
import employeeBenefitFundsApi from "@/services/employeeBenefitFundsApi";
import FundPageHeader from "./components/FundPageHeader.vue";
import { apiError, envelopeData } from "./utils";

const formRef = ref(null);
const valid = ref(false);
const loading = ref(false);
const saving = ref(false);
const loaded = ref(false);
const setupComplete = ref(false);
const form = reactive({
  employee_pf_rate: 3,
  company_pf_rate: 3,
  pf_vesting_years: 3,
  gratuity_eligibility_years: 10,
  medical_claims_per_year: 2,
  medical_claim_limit: 20000,
  loan_basic_salary_multiplier: 3,
  status: true,
});

const rules = {
  required: (value) => value !== null && value !== "" && value !== undefined || "Required",
  nonNegative: (value) => Number(value) >= 0 || "Must be zero or greater",
  positive: (value) => Number(value) > 0 || "Must be greater than zero",
  percentage: (value) => (Number(value) >= 0 && Number(value) <= 100) || "Enter a value from 0 to 100",
};

const applySettings = (settings = {}) => {
  Object.keys(form).forEach((key) => {
    if (settings[key] !== undefined && settings[key] !== null) form[key] = settings[key];
  });
  form.status = Boolean(form.status);
};

const loadSettings = async () => {
  loading.value = true;
  try {
    const data = envelopeData(await employeeBenefitFundsApi.settings()) || {};
    applySettings(data.settings);
    setupComplete.value = Boolean(data.setup_complete);
    loaded.value = true;
  } catch (exception) {
    toast.error(apiError(exception, "Unable to load fund settings."));
  } finally {
    loading.value = false;
  }
};

const saveSettings = async () => {
  const validation = await formRef.value?.validate();
  if (!validation?.valid) return;
  saving.value = true;
  try {
    const data = envelopeData(await employeeBenefitFundsApi.updateSettings({ ...form }));
    applySettings(data);
    toast.success("Fund settings updated successfully.");
    await loadSettings();
  } catch (exception) {
    toast.error(apiError(exception, "Unable to save fund settings."));
  } finally {
    saving.value = false;
  }
};

onMounted(loadSettings);
</script>
